import { MutableRefObject, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

// BrowserRouter v6 does not expose useBlocker. Guard its navigator for app links
// and history events for browser/device back, keeping the editor mounted on cancel.
export default function useScheduleNavigationGuard(
  dirty: MutableRefObject<boolean>,
  message: string,
) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  useEffect(() => {
    const push = navigator.push;
    const replace = navigator.replace;
    let index = window.history.state?.idx ?? 0;
    let restoring = false;
    const allow = () => {
      if (!dirty.current) return true;
      // eslint-disable-next-line no-alert
      if (!window.confirm(message)) return false;
      // The ref belongs to this editor and must clear before navigation.
      // eslint-disable-next-line no-param-reassign
      dirty.current = false;
      return true;
    };
    navigator.push = (...args) => {
      if (allow()) push.apply(navigator, args);
    };
    navigator.replace = (...args) => {
      if (allow()) replace.apply(navigator, args);
    };
    const pop = (event: PopStateEvent) => {
      const next = event.state?.idx ?? 0;
      if (restoring) {
        restoring = false;
        event.stopImmediatePropagation();
        return;
      }
      if (!allow()) {
        event.stopImmediatePropagation();
        restoring = true;
        window.history.go(index - next);
      } else index = next;
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (dirty.current) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      }
    };
    window.addEventListener('popstate', pop, true);
    window.addEventListener('beforeunload', unload);
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      window.removeEventListener('popstate', pop, true);
      window.removeEventListener('beforeunload', unload);
    };
  }, [dirty, message, navigator]);
}
